// @ts-check

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Banner, BlockStack, Box, Button, Card, Divider, FormLayout, InlineStack, Layout, MediaCard, Page, Spinner, Text, TextField } from "@shopify/polaris";
import { LogoFacebookIcon } from "@shopify/polaris-icons";

import { usePostHog } from "posthog-js/react";

import { ExternalPlatformConnection } from "components/ExternalPlatformConnection/ExternalPlatformConnection";

import { useGlobalContext } from "context/GlobalContext";
import { useSettings } from "context/SettingsContext";
import { usePlatforms } from "context/PlatformsContext";

import lurkLogo from "assets/lurk-logo.png";

export default function SettingsPage() {
  const navigate = useNavigate();
  const posthog = usePostHog();
  const { dates, store, isFetchingStore, isRefetchingStore } = useGlobalContext();
  const { error, setError } = useSettings();

  const { connectablePlatforms, isFetchingConnectablePlatforms } = usePlatforms();
  
  const [shopUrl, setShopUrl] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [orderSyncLoading, setOrderSyncLoading] = useState(false);
  const [facebookSyncLoading, setFacebookSyncLoading] = useState(false);
  const [orderAttributionLoading, setOrderAttributionLoading] = useState(false);
  const [syncFacebookStatsLoading, setSyncFacebookStatsLoading] = useState(false);
  const [adSummaryCalculationLoading, setAdSummaryCalculationLoading] = useState(false);
  const [facebookStatsLoading, setFacebookStatsLoading] = useState(false);
  const [bulkJobsLoading, setBulkJobsLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);

  useEffect(() => {
    posthog.capture("$pageview");

    return () => {
      setError(null);
    }
  }, []);

  const handleChangePlanClick = () => {
    navigate("/settings/plans");
  };

  let planNameToRender;
  if (isFetchingStore || isRefetchingStore) {
    planNameToRender = (
      <Text as="span" tone="subdued">Loading...</Text>
    );
  } else if (store?.subscription?.plan) {
    planNameToRender = (
      <Text as="span" fontWeight="semibold">{store.subscription.plan.name}</Text>
    );
  } else {
    planNameToRender = (
      <Text as="span" tone="subdued">No plan selected</Text>
    );
  }

  const handleTriggerOrderSync = async () => {
    setOrderSyncLoading(true);

    return fetch("/api/admin/trigger-order-sync", {
      method: "POST",
      body: JSON.stringify({ shopUrl }),
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((response) => {
        console.log("resposne", response);
      })
      .finally(() => {
        setOrderSyncLoading(false);
      });
  };

  const handleTriggerFacebookSync = async () => {
    setFacebookSyncLoading(true);

    return fetch("/api/admin/trigger-facebook-sync", {
      method: "POST",
      body: JSON.stringify({ shopUrl }),
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((response) => {
        console.log("resposne", response);
      })
      .finally(() => {
        setFacebookSyncLoading(false);
      });
  };

  const handleSyncFacebookStats = async () => {
    setSyncFacebookStatsLoading(true);

    return fetch(`/api/admin/sync-facebook-stats`, {
      method: "POST",
      body: JSON.stringify({
        shopUrl,
        start_date: startDate,
        end_date: endDate,
      }),
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((response) => {
        console.log("resposne", response);
      })
      .finally(() => {
        setSyncFacebookStatsLoading(false);
      });
  };

  const handleFetchFacebookStats = async () => {
    setFacebookStatsLoading(true);

    return fetch(`/api/admin/fetch-facebook-stats?start_date=${dates.start.toISOString()}&end_date=${dates.end.toISOString()}`, {
      method: "POST",
      body: JSON.stringify({ shopUrl }),
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((response) => {
        console.log("resposne", response);
      })
      .finally(() => {
        setFacebookStatsLoading(false);
      });
  };

  const handleTriggerOrderAttribution = async () => {
    setOrderAttributionLoading(true);

    return fetch("/api/admin/trigger-order-attribution", {
      method: "POST",
      body: JSON.stringify({ shopUrl }),
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((response) => {
        console.log("resposne", response);
      })
      .finally(() => {
        setOrderAttributionLoading(false);
      });
  };

  const handleFinishPendingBulkJobs = async () => {
    setBulkJobsLoading(true);

    return fetch("/api/admin/finish-pending-bulk-jobs", {
      method: "POST",
      body: JSON.stringify({ shopUrl }),
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((response) => {
        console.log("resposne", response);
      })
      .finally(() => {
        setBulkJobsLoading(false);
      });
  };

  const handleTest = async () => {
    setTestLoading(true);

    return fetch("/api/admin/test", {
      method: "POST",
      body: JSON.stringify({ shopUrl }),
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((response) => {
        console.log("resposne", response);
      })
      .finally(() => {
        setTestLoading(false);
      });
  };

  const handleStartDateChange = (e) => {
    const htmlDate = e.target.value;
    if (htmlDate) {
      setStartDate(htmlDate);
    } else {
      setStartDate('');
    }
  };

  const handleEndDateChange = (e) => {
    const htmlDate = e.target.value;
    if (htmlDate) {
      setEndDate(htmlDate);
    } else {
      setEndDate('');
    }
  };

  const handleLurkLearnMoreClick = () => {
    posthog.capture("click-partner-learn-more", {
      partner: "lurk",
      location: "settings-page",
    });

    window.open("http://apps.shopify.com/lurk?utm_source=origin-tracking&utm_medium=partners&utm_campaign=settings-page", "_blank");
  };

  let errorToRender;
  if (error) {
    errorToRender = (
      <Layout.Section>
        <Banner title="Error" tone="critical">
          <Text as="p">{error}</Text>
        </Banner>
      </Layout.Section>
    );
  }

  let connectablePlatformsToRender;
  if (isFetchingConnectablePlatforms) {
    connectablePlatformsToRender = (
      <Card>
        <Spinner size="small" />
      </Card>
    );
  } else {
    connectablePlatformsToRender = connectablePlatforms.map((connectablePlatform) => (
      <ExternalPlatformConnection
        key={connectablePlatform.id}
        platform={connectablePlatform}
      />
    ));
  }

  return (
    <Page title="Settings">
      <Layout>
        {errorToRender}

        <Layout.AnnotatedSection
          title="Billing"
          description="Manage your billing information and plan"
        >
          <Card>
            <FormLayout>
              <InlineStack align="space-between" blockAlign="center">
                <InlineStack>
                  <Text as="p">
                    Current plan:{" "}
                    {planNameToRender}
                  </Text>
                </InlineStack>

                <Button onClick={handleChangePlanClick}>Change plan</Button>
              </InlineStack>
            </FormLayout>
          </Card>
        </Layout.AnnotatedSection>

        <Layout.AnnotatedSection
          title="Reports"
          description="Manage your insights"
        >
          <Card>
            <FormLayout>
              <InlineStack align="space-between" blockAlign="start">
                <BlockStack>
                  <Text as="p" variant="bodyMd" fontWeight="semibold">
                    Timezone
                  </Text>
                  <Text as="p" variant="bodyMd" tone="subdued">
                    All reports are shown in your store's local timezone
                  </Text>
                </BlockStack>

                <Text as="p" variant="bodyMd">
                  {store?.iana_timezone}
                </Text>
              </InlineStack>
            </FormLayout>
          </Card>
        </Layout.AnnotatedSection>

        <Layout.AnnotatedSection
          title="Linked accounts"
          description="Link your external accounts to Origin"
        >
          <BlockStack gap="200">
            {connectablePlatformsToRender}
          </BlockStack>
        </Layout.AnnotatedSection>

        <Layout.Section variant="oneThird">
          <Box paddingBlockStart="400">
            <BlockStack>
              <Text as="h2" variant="headingMd">
                Partners
              </Text>

              <Text as="p" variant="bodyMd">
                Explore some of our partners
              </Text>
            </BlockStack>
          </Box>
        </Layout.Section>

        <Layout.Section>
          <Box paddingBlockStart="400">
            <Card padding="0">
              <div
                style={{
                  display: "flex",
                  justifyContent: "start",
                  height: "140px",
                }}
              >
                <img
                  alt="Lurk: Competitor Price Tracking"
                  height="100%"
                  style={{
                    objectFit: "contain",
                    objectPosition: "center",
                    flexGrow: "0"
                  }}
                  src={lurkLogo}
                />

                <Box padding="400">
                  <BlockStack gap="400" inlineAlign="start">
                    <BlockStack>
                      <Text as="h3" variant="headingMd">
                        Lurk: Competitor Price Tracking
                      </Text>
                      <Text as="p" variant="bodyMd" tone="subdued">
                        Automatically track your competitors' prices and adjust your own to stay competitive.
                      </Text>
                    </BlockStack>

                    <Button
                      onClick={handleLurkLearnMoreClick}
                    >
                      Learn more
                    </Button>
                  </BlockStack>
                </Box>
              </div>
            </Card>
          </Box>
        </Layout.Section>

        {store?.shop === "quickstart-5f71dd43.myshopify.com" && (
          <Layout.AnnotatedSection
            title="Admin"
          >
            <Card>
              <FormLayout>
                <TextField
                  label="Shop URL"
                  value={shopUrl}
                  onChange={setShopUrl}
                  autoComplete=""
                />

                <Divider />

                <Button
                  onClick={handleFinishPendingBulkJobs}
                  loading={bulkJobsLoading}
                >
                  Finish pending bulk order jobs
                </Button>

                <Divider />

                <Button onClick={handleTriggerOrderSync} loading={orderSyncLoading}>
                  Trigger order sync
                </Button>

                <Divider />

                <Button
                  onClick={handleTriggerFacebookSync}
                  loading={facebookSyncLoading}
                  icon={LogoFacebookIcon}
                >
                  syncFacebook
                </Button>

                <Divider />

                <InlineStack gap="400" blockAlign="end">
                  <BlockStack>
                    <label>Start date:</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={handleStartDateChange}
                    />
                  </BlockStack>
                  <BlockStack>
                    <label>End date:</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={handleEndDateChange}
                    />
                  </BlockStack>
                </InlineStack>
                
                <Button
                  onClick={handleSyncFacebookStats}
                  loading={syncFacebookStatsLoading}
                  icon={LogoFacebookIcon}
                >
                  syncFacebookStatsDateRange
                </Button>

                <Divider />

                <InlineStack gap="400" blockAlign="end">
                  <BlockStack>
                    <label>Start date:</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={handleStartDateChange}
                    />
                  </BlockStack>
                  <BlockStack>
                    <label>End date:</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={handleEndDateChange}
                    />
                  </BlockStack>
                </InlineStack>

                <Divider />

                {/* <Button onClick={handleFetchFacebookStats} loading={facebookStatsLoading} icon={LogoFacebookIcon}>
                  Fetch Facebook stats
                </Button> */}
                <Button
                  onClick={handleTriggerOrderAttribution}
                  loading={orderAttributionLoading}
                  icon={LogoFacebookIcon}
                >
                  Trigger order attribution
                </Button>

                <Divider />

                <Button
                  onClick={handleTest}
                  loading={testLoading}
                >
                  Test
                </Button>
              </FormLayout>
            </Card>
          </Layout.AnnotatedSection>
        )}
      </Layout>
    </Page>
  );
};
